import { useEffect, useMemo, useState, useCallback } from "react";
import "../../styles/ManualWinner.css";
import { FiPlus } from "react-icons/fi";
import moment from "moment";
import toast from "react-hot-toast";
import { MdEdit, MdDelete } from "react-icons/md";
import Loader from "../../components/Loader";
import useActiveGames from "../../hooks/useActiveGames";
import NoData from "../../components/NoData";
import GameSelect from "../../components/GameSelect";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import JackpotWinnerServices from "../../services/JackpotWinnerServices";
import CreateJackpotWinnerModal from "./modals/CreateJackpotWinnerModal";
import EditJackpotWinnerModal from "./modals/EditJackpotWinnerModal";
import LiveSessionServices from "../../services/LiveSessionServices";

// Up to this many games fit as a row of tabs; past it a picker takes over.
const MAX_GAME_TABS = 3;

const Jackpot = () => {
  const liveSSApi = new LiveSessionServices();
  const manualWinnerApi = new JackpotWinnerServices();

  const nowUTC = moment().format("YYYY-MM-DD");
  const {
    games: activeGames,
    loading: gamesLoading,
    activeCode: activeGame,
    setActiveCode: setActiveGame,
  } = useActiveGames({ remember: false });

  /** Display name of the selected game, for modal titles. */
  const activeGameLabel =
    activeGames.find((g) => g.game_code === activeGame)?.game_name || activeGame;
  const [selectedDate, setSelectedDate] = useState(nowUTC);
  const [openJackpotWinnerModal, setOpenJackpotWinnerModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [manualSessionList, setJackpotSessionList] = useState([]);
  const [editMWModal, setEditMWModal] = useState(false);
  const [editMWData, setEditMWData] = useState(null);
  const [yantraList, setYantraList] = useState([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const yantraNameById = useMemo(() => {
    const m = new Map();
    (yantraList || []).forEach((y) => {
      const id = Number(y?.id);
      if (Number.isFinite(id)) m.set(id, y?.yantra_name || `Yantra ${id}`);
    });
    return m;
  }, [yantraList]);

  const getYantraData = async (gameCode = activeGame) => {
    if (!gameCode) return;
    liveSSApi
      .getYantraListData({ game: gameCode })
      .then((response) => {
        if (response.isSuccess) {
          const yantras = response?.data || [];
          setYantraList(yantras);
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error get getYantraListData:", error);
        toast.dismiss();
        toast.error("Error get yantra list data");
      })
      .finally(() => {
      });
  };

  const getJackpotWinnerData = useCallback(async () => {
    // The game list arrives a moment after mount, so activeGame is empty on
    // the first render. Firing then just asks the server for game "" and
    // comes back as "Game type is required".
    if (!activeGame) return;
    const filter = `page=${page}&limit=${limit}&game=${activeGame}&date_filter=${selectedDate}`;
    setLoading(true);
    manualWinnerApi
      .getJackpotSessionList(filter)
      .then((response) => {
        if (response.isSuccess) {
          setJackpotSessionList(response.data || []);
          setPagination(response.pagination);
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error get getJackpotSessionList:", error);
        toast.dismiss();
        toast.error("Error get manual winner sessions list!");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, limit, activeGame, selectedDate]);

  const openEditModal = (data) => {
    setEditMWModal(true);
    setEditMWData(data);
  };
  const openDeleteModal = (data) => {
    setDeleteModalOpen(true);
    setEditMWData(data);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    manualWinnerApi
      .deleteJackpotWinnerSession(editMWData.id)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
          getJackpotWinnerData();
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error deleteJackpotWinnerSession:", error);
        const backendMessage =
          error?.response?.data?.message ||
          "Failed to delete manual winner session";

        toast.dismiss();
        toast.error(backendMessage);
      })
      .finally(() => {
        setDeleteLoading(false);
        setDeleteModalOpen(false);
        setEditMWData(null);
      });
  };

  useEffect(() => {
    getJackpotWinnerData();
  }, [getJackpotWinnerData]);

  // The board belongs to the selected game, so it has to be refetched when
  // the tab changes - not once on mount.
  useEffect(() => {
    getYantraData(activeGame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGame]);

  return (
    <div>
      <div className="manual-winner-header">
        {/* LEFT: GAME TYPE TABS */}
        <div className="mw-left">
          {gamesLoading ? (
            <span className="mw-tabs-empty">Loading games...</span>
          ) : activeGames.length === 0 ? (
            <span className="mw-tabs-empty">
              No games are running. Start one in Settings &rsaquo; Games.
            </span>
          ) : activeGames.length > MAX_GAME_TABS ? (
            <GameSelect
              games={activeGames}
              value={activeGame}
              onChange={setActiveGame}
              suffix="Session"
            />
          ) : (
            activeGames.map((g) => (
              <button
                key={g.id}
                className={`mw-tab ${activeGame === g.game_code ? "active" : ""}`}
                onClick={() => setActiveGame(g.game_code)}
                title={`${g.total_ghar} ghar`}
              >
                {g.game_name} Session
              </button>
            ))
          )}
        </div>

        {/* RIGHT: FILTERS + ACTION */}
        <div className="mw-right">
          {/* DATE FILTER */}
          <input
            type="date"
            className="mw-date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />

          {/* CREATE BUTTON */}
          <button
            className="mw-create-btn"
            onClick={() => setOpenJackpotWinnerModal(true)}
          >
            <FiPlus size={18} className="me-1" /> Add
          </button>
        </div>
      </div>

      <div>
        {loading ? (
          <Loader message="Loading Multi Winner Session..." />
        ) : manualSessionList.length === 0 ? (
          <NoData
            message="No Multi Winner Session Found"
            subText="You haven't added any multi winner session yet. Click on Add to get started."
          />
        ) : (
          <>
            <div className="mw-multi-table-header">
              <div className="mw-col-session">Session No</div>
              <div className="mw-col-game">Session</div>
              <div className="mw-col-type">Type</div>
              <div className="mw-col-status">Status</div>
              <div className="mw-col-action">Action</div>
            </div>

            <div className="mw-list">
              {manualSessionList.map((s) => (
                <div className="mw-multi-row" key={s.id}>
                  <div className="mw-col mw-col-session">
                    <span className="mw-label">Session No</span>
                    <div className="session-no"># {s.session_no}</div>
                    <div className="time">
                      {moment.utc(s.start_time).local().format("hh:mm A")} –{" "}
                      {moment.utc(s.end_time).local().format("hh:mm A")}
                    </div>
                  </div>

                  <div className="mw-col mw-col-game">
                    <span className="mw-label">Session</span>
                    <span className="badge game">{s.game_type}</span>
                  </div>

                  <div className="mw-col mw-col-type">
                    <span className="mw-label">Type</span>
                    <span className="badge manual">
                      {s.winner_type === "JACKPOT" ? "Multi Winner" : "--"} ({s.winner_count})
                    </span>
                    <div>
                      {(() => {
                        const ids = String(s?.winner_yantra || "")
                          .split(",")
                          .map((v) => Number(String(v).trim()))
                          .filter((n) => Number.isFinite(n) && n > 0);
                        if (!ids.length) return <span>-</span>;
                        const names = ids.map(
                          (id) => yantraNameById.get(id) || `Yantra ${id}`,
                        );
                        return names.reduce((acc, name, i) => {
                          if (i) {
                            acc.push(
                              <span key={`sep-${s.id}-${i}`}> | </span>,
                            );
                          }
                          acc.push(
                            <span key={`y-${s.id}-${i}`} className="badge-yantra-name">
                              {name}
                            </span>,
                          );
                          return acc;
                        }, []);
                      })()}
                    </div>
                  </div>

                  <div className="mw-col mw-col-status">
                    <span className="mw-label">Status</span>
                    <span className={`status ${s.status.toLowerCase()}`}>
                      {s.status}
                    </span>
                  </div>

                  <div className="mw-col mw-col-action">
                    <span className="mw-label">Action</span>
                    <div className="mw-actions">
                      {(s.status === "PENDING" || s.status === "RUNNING") ? (
                        <>
                          <button
                            className="icon-btn edit me-2"
                            onClick={() => {
                              openEditModal(s);
                            }}
                          >
                            <MdEdit size={22} />
                          </button>
                          <button
                            className="icon-btn delete"
                            onClick={() => {
                              openDeleteModal(s);
                            }}
                          >
                            <MdDelete size={22} />
                          </button>
                        </>
                      ) : (
                        <>-</>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {pagination && pagination.totalPages > 1 && (
              <div className="modern-pagination">
                {/* Limit Selector */}
                <div className="mp-limit">
                  <span>Show</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Navigation */}
                <div className="mp-controls">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Prev
                  </button>

                  <div className="mp-info">
                    Page <strong>{pagination.page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong>
                  </div>

                  <button
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>

                {/* Jump to Page */}
                <div className="mp-jump">
                  <select
                    value={page}
                    onChange={(e) => setPage(e.target.value)}
                  >
                    {Array.from({ length: pagination.totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CreateJackpotWinnerModal
        show={openJackpotWinnerModal}
        setShow={setOpenJackpotWinnerModal}
        activeGame={activeGame}
        gameLabel={activeGameLabel}
        yantraList={yantraList}
        getJackpotWinnerData={getJackpotWinnerData}
      />

      <EditJackpotWinnerModal
        editMWModal={editMWModal}
        setEditMWModal={setEditMWModal}
        editMWData={editMWData}
        setEditMWData={setEditMWData}
        activeGame={activeGame}
        gameLabel={activeGameLabel}
        yantraList={yantraList}
        getJackpotWinnerData={getJackpotWinnerData}
      />

      <ConfirmDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Session"
        message={`Are you sure you want to delete this multi winner session(${editMWData?.session_no})?`}
        loading={deleteLoading}
      />
    </div>
  );
};

export default Jackpot;
